import type { StoryblokStory } from 'storyblok-generate-ts'

type Story = StoryblokStory<Record<string, any>>

export const shapeBlogArticleStory = <T extends Record<string, any>>(
  story: StoryblokStory<T> | null | undefined,
): StoryblokStory<T> | null | undefined =>
  shapeBlogStory(story, {
    shapeContent: (content) => ({
      ...content,
      tags: shapeTags(content.tags),
      teasers: shapeRelatedArticles(content.teasers),
      more_articles: shapeRelatedArticles(content.more_articles),
    }),
  }) as StoryblokStory<T> | null | undefined

export const shapeBlogOverviewStory = <T extends Record<string, any>>(
  story: StoryblokStory<T> | null | undefined,
): StoryblokStory<T> | null | undefined =>
  shapeBlogStory(story, {
    shapeContent: (content) => ({
      ...content,
      tags: shapeTags(content.tags),
      more_articles: shapeRelatedArticles(content.more_articles),
    }),
  }) as StoryblokStory<T> | null | undefined

const shapeBlogStory = (
  story: Story | null | undefined,
  options: {
    shapeContent: (content: Record<string, any>) => Record<string, any>
  },
): Story | null | undefined => {
  if (!story?.content) {
    return story
  }

  return {
    ...story,
    content: options.shapeContent(story.content),
  }
}

const shapeRelatedArticles = (articles: unknown) => {
  if (!Array.isArray(articles)) {
    return articles
  }

  return articles.map((article) =>
    typeof article === 'string'
      ? article
      : shapeRelatedArticle(article as Story),
  )
}

const shapeRelatedArticle = (article: Story): Story => {
  const content = article.content || {}

  return {
    id: article.id,
    uuid: article.uuid,
    name: article.name,
    full_slug: article.full_slug,
    content: {
      _uid: content._uid,
      component: content.component,
      title: content.title,
      teaser_title: content.teaser_title,
      teaser_description: content.teaser_description,
      teaser_image: content.teaser_image,
      image: content.image,
      tags: shapeTags(content.tags),
      content: shapeFallbackContent(content),
    },
  } as unknown as Story
}

const shapeFallbackContent = (content: Record<string, any>) => {
  if (content.teaser_description || !Array.isArray(content.content)) {
    return undefined
  }

  const fallbackText = content.content.find(
    (item: Record<string, any>) => item.component === 'CmsText',
  )

  return fallbackText ? [fallbackText] : undefined
}

const shapeTags = (tags: unknown) => {
  if (!Array.isArray(tags)) {
    return tags
  }

  return tags.map((tag) =>
    typeof tag === 'string' ? tag : shapeTag(tag as Story),
  )
}

const shapeTag = (tag: Story): Story =>
  ({
    uuid: tag.uuid,
    name: tag.name,
    translated_slugs: tag.translated_slugs,
    content: {
      title: tag.content?.title,
    },
  }) as unknown as Story
